import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import { Grid, Typography } from "@material-ui/core";
import Button from "components/CustomButtons/Button";
import dayjs from "dayjs";
import MUIButton from "@material-ui/core/Button";
import { getName } from "country-list";
import ClientDetails from "./Modals/ClientDetails";
import { typography } from "@material-ui/system";
import AdminClientCard from "./AdminClientCard";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  align?: "center" | "left" | "right";
  format?: (value: number) => React.ReactNode;
}

interface ClientData {
  email?: React.ReactNode;
  first_name?: string;
  groups?: unknown[];
  hasAssessments?: boolean;
  hasPending?: boolean;
  last_name?: string;
  lms_accessed?: boolean;
  start_date?: string;
  user?: React.ReactNode;
  [key: string]: unknown;
}

const columns: Column[] = [
  { id: "name", label: "Name", minWidth: 170, maxWidth: 200 },
  { id: "email", label: "Email", minWidth: 100, maxWidth: 250 },
  {
    id: "group",
    label: "Group(s)",
    minWidth: 130,
    maxWidth: 160,
    align: "center",
  },
  {
    id: "user",
    label: "Coach",
    minWidth: 120,
    maxWidth: 170,
    align: "center",
  },
  {
    id: "start",
    label: "Enrol Date",
    maxWidth: 170,
    minWidth: 120,
    align: "center",
  },
  {
    id: "details",
    label: "Assigned",
    maxWidth: 130,
    minWidth: 120,
    align: "center",
  },
];

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  container: {
    maxHeight: 440,
  },
});

interface ClientsTableProps {
  data: ClientData[];
  isMobile?: boolean;
}

export default function StickyHeadTable(props: ClientsTableProps): React.ReactElement {
  const classes = useStyles();
  const [page, setPage] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [client, setClient] = React.useState<ClientData>({});
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { data } = props;
  let rows: Array<Record<string, React.ReactNode>> = [];

  data.forEach((client: ClientData) => {
    rows.push({
      name:
        client.first_name || client.last_name
          ? client.first_name + " " + client.last_name
          : "Not Specified",
      email: client.email,
      group: client.groups!.length ? (
        <Grid
          container
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          {client.groups!.map((group) => {
            return (
              <Grid item style={{ margin: 5 }}>
                <Typography
                  align='center'
                  style={{
                    borderRadius: 10,
                    paddingLeft: 10,
                    paddingRight: 10,
                    background: "#40628d",
                    display: "flex",
                    fontSize: 12,
                    fontWeight: 400,
                    color: "#f7f7f7",
                  }}
                >
                  {group}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        "N/A"
      ),
      user: client.user,
      start: dayjs(client.start_date).format("DD/MM/YYYY"),
      details: client.hasAssessments ? (
        <MUIButton
          variant='outlined'
          size='small'
          onClick={() => {
            setClient(client);
            setOpen(true);
          }}
          style={{ border: "1px solid #abcaad", color: "#6fb472" }}
        >
          View
        </MUIButton>
      ) : (
        <typography
          align='center'
          style={{
            color: "#af4343",

            fontSize: 13,
          }}
        >
          None Assigned
        </typography>
      ),
    });
  });
  rows = rows.reverse();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return props.isMobile ? (
    <div>
      {data
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((client, index) => (
          <AdminClientCard
            key={(client.email || index) as React.Key}
            client={client}
            onViewAssigned={() => {
              setClient(client);
              setOpen(true);
            }}
          />
        ))}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        style={{ marginTop: 16, background: "#fafafa", borderRadius: 4 }}
        component='div'
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ClientDetails client={client} open={open} setOpen={setOpen} />
    </div>
  ) : (
    <Paper className={classes.root} variant='outlined'>
      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    fontSize: 17,
                    background: " #edf7f2",
                    borderRight: "1px solid #e3e3e3",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow role='checkbox' tabIndex={-1} key={index}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            maxWidth: column.maxWidth,
                            borderRight: "1px solid #e3e3e3",
                            paddingTop: 8,
                            paddingBottom: 8,
                          }}
                        >
                          {column.format && typeof value === "number"
                            ? column.format(value)
                            : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <ClientDetails client={client} open={open} setOpen={setOpen} />
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 100]}
        style={{ borderTop: "1px solid #e3e3e3", background: "#edf7f2" }}
        component='div'
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}
